import { ChangeEvent, FC, MouseEvent, useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, FormControl, InputAdornment, Typography } from "@mui/material";
import { Close, Search, Tune } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { blue } from "@mui/material/colors";

import { IFilterDto } from "@framework/types";
import { FilterStatus, useAsu } from "@framework/provider-asu";

import DoubleSemafor from "../../../../assets/images/double-semafor.svg";
import DoubleBlueSemafor from "../../../../assets/images/double-blue-semafor.svg";
import TripleSemafor from "../../../../assets/images/triple-semafor.svg";

import { FilterDialog } from "../../../dialogs/filter";
import { PanelButton } from "../styled/panel-button";
import { StyledAccordion } from "../styled/accordion";
import { StyledAccordionDetails } from "../styled/accordion-details";
import { StyledAccordionSummary } from "../styled/accordion-summary";
import { StyledToolbar } from "../styled/toolbar";
import { ToolbarButton } from "../styled/toolbar-button";
import { ListTreeView } from "./tree-menu";
import { SearchTreeView } from "./search-menu";
import { StyledSearchInput } from "./styled";

const getSearchQuery = (value: string): string | null => value.trim() || null;
const SEARCH_QUERY_DEBOUNCE_MS = 1000;

export const Equipment: FC = () => {
  const {
    query,
    updateQuery,
    isLoading,
    isMenuLoadFailed,
    retryMenu,
    searchList,
    updateFilters,
    isFilterApplied,
    resetFilters,
    list,
    showNonWarningStatusFilter,
    showWarningStatusFilter,
    showAllStatusFilter,
    currentStatusFilter,
  } = useAsu();

  const { formatMessage } = useIntl();

  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(!!query);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(query ?? "");
  const previousQueryRef = useRef<string | null>(query);

  const hasMenuData = !!list?.length;
  const showMenuLoading = isLoading && !hasMenuData;

  const handleSearchToggle = () => {
    setIsSearchOpen(prev => !prev);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
  };

  const updateQueryDebounced = useDebouncedCallback((value: string) => {
    updateQuery(value);
  }, SEARCH_QUERY_DEBOUNCE_MS);

  const handleChangeQuery = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    setSearchValue(value);

    if (!getSearchQuery(value)) {
      updateQueryDebounced.cancel();
      updateQuery(null);
      return;
    }

    updateQueryDebounced(value);
  };

  const openFilters = () => {
    setIsFiltersOpen(true);
  };

  const closeFilters = () => {
    setIsFiltersOpen(false);
  };

  const applyFilters = (values: IFilterDto) => {
    const appliedFilters = Object.keys(values).filter(key => {
      const value = values[key as keyof IFilterDto];
      return !!value;
    });

    if (!appliedFilters.length) {
      resetFilters();
    } else {
      updateFilters(values);
    }

    closeFilters();

    return Promise.resolve();
  };

  const stopPropagation = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const closeSearch = () => {
    updateQueryDebounced.cancel();
    setSearchValue("");
    updateQuery(null);
    setIsSearchOpen(false);
  };

  const onEscapePress = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      closeSearch();
    }
  };

  useEffect(() => {
    const previousQuery = previousQueryRef.current;
    previousQueryRef.current = query;

    if (query) {
      setIsSearchOpen(true);
    }

    setSearchValue(currentValue => {
      if (!query) {
        return "";
      }

      const currentQuery = getSearchQuery(currentValue);

      if (!currentQuery || currentQuery === previousQuery || currentQuery === query) {
        return query;
      }

      return currentValue;
    });
  }, [query]);

  useEffect(() => {
    return () => {
      updateQueryDebounced.cancel();
    };
  }, [updateQueryDebounced]);

  useEffect(() => {
    if (isSearchOpen) {
      document.addEventListener("keydown", onEscapePress);
    }

    return () => {
      document.removeEventListener("keydown", onEscapePress);
    };
  }, [isSearchOpen]);

  return (
    <>
      <StyledAccordion elevation={0} expanded={true}>
        <StyledAccordionSummary
          aria-controls="equipment-content"
          id="equipment-header"
          sx={{
            backgroundColor: "#FFFFFF",
            border: "none",
            pb: 0,
            "&.Mui-focusVisible": { backgroundColor: "#FFFFFF" },
            "& .MuiAccordionSummary-content.Mui-expanded": { mb: 0 },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            {isSearchOpen ? (
              <StyledToolbar sx={{ p: 0, "&.MuiToolbar-root": { minHeight: 26 } }} onClick={stopPropagation}>
                <FormControl
                  variant="filled"
                  fullWidth
                  sx={{ height: 26, fontSize: 16, fontWeight: 600, lineHeight: "19px", zIndex: 2001 }}
                >
                  <StyledSearchInput
                    id="search"
                    type="text"
                    placeholder={formatMessage({ id: "form.labels.search" })}
                    onChange={handleChangeQuery}
                    value={searchValue}
                    autoFocus
                    endAdornment={
                      <InputAdornment position="end">
                        <PanelButton sx={{ svg: { color: "#0042B0" } }} aria-label="clear" onClick={closeSearch}>
                          <Close />
                        </PanelButton>
                      </InputAdornment>
                    }
                  />
                </FormControl>
              </StyledToolbar>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography fontWeight={700} fontSize={18} lineHeight="21px" color="#363846">
                  <FormattedMessage id="components.mainMenu.equipments" />
                </Typography>
                <Box>
                  <PanelButton
                    onClick={openFilters}
                    sx={{ background: isFilterApplied ? blue[400] : "transparent", svg: { color: "#0042B0" } }}
                  >
                    <Tune />
                  </PanelButton>
                  <PanelButton onClick={handleSearchToggle} sx={{ ml: 2, svg: { color: "#0042B0" } }}>
                    <Search />
                  </PanelButton>
                </Box>
              </Box>
            )}
          </Box>
        </StyledAccordionSummary>
        <StyledAccordionDetails>
          <StyledToolbar
            sx={{ pt: 1, pb: 1.5, px: 2, borderBottom: "1px solid #EBEBF7", "&.MuiToolbar-root": { minHeight: 36 } }}
          >
            <ToolbarButton
              active={currentStatusFilter === FilterStatus.all}
              onClick={showAllStatusFilter}
              viewBox="0 0 36 8"
              icon={<TripleSemafor />}
              iconHeight={8}
              iconWidth={42}
            />
            <ToolbarButton
              active={currentStatusFilter === FilterStatus.warning}
              onClick={showWarningStatusFilter}
              viewBox="0 0 22 8"
              icon={<DoubleSemafor />}
              iconHeight={8}
              iconWidth={22}
            />
            <ToolbarButton
              active={currentStatusFilter === FilterStatus.nonWarning}
              onClick={showNonWarningStatusFilter}
              viewBox="0 0 22 8"
              icon={<DoubleBlueSemafor />}
              iconHeight={8}
              iconWidth={22}
            />
          </StyledToolbar>
          {isMenuLoadFailed && (
            <Alert
              severity="error"
              sx={{ m: 2 }}
              action={
                <Button color="inherit" size="small" onClick={retryMenu} disabled={isLoading}>
                  <FormattedMessage id="form.buttons.retry" />
                </Button>
              }
            >
              <FormattedMessage id="components.mainMenu.loadError" />
            </Alert>
          )}
          {showMenuLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={70}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <>
              {searchList !== null && <SearchTreeView onSelect={handleSearchClose} />}
              {searchList === null && list && list.length > 0 && <ListTreeView stations={list} />}
            </>
          )}
        </StyledAccordionDetails>
      </StyledAccordion>
      <FilterDialog open={isFiltersOpen} onClose={closeFilters} onConfirm={applyFilters} />
    </>
  );
};
